import { useState, useEffect } from "react";
import { supabase } from '../supabase'; 
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { v4 as uuidv4 } from 'uuid';

export default function LibraryModal({ open, onClose, onSelect }) {
    const [medias, setMedias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("library"); // "library", "emoji", or "upload"
    const [uploading, setUploading] = useState(false);

    const fetchMedias = async () => {
        if (!open || tab !== "library") return;
        setLoading(true);
        const { data: session } = await supabase.auth.getSession();
        const userId = session?.session?.user?.id;

        if (!userId) {
            setMedias([]);
            setLoading(false);
            return;
        }

        const { data, error } = await supabase
            .from('medias')
            .select('*')
            .order('created_at', { ascending: false })
            .eq('user_uuid', userId);

        if (error) {
            setMedias([]);
        } else {
            setMedias(data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchMedias();
    }, [open, tab]);

    // Image upload handler
    async function handleImageUpload(e) {
        if (e.target.files.length === 0) {
            return;
        }

        if (e.target.files[0].size > 1024 * 1024 * 15) {
            alert('O arquivo deve ter no máximo 15MB');
            return;
        }

        if (e.target.files[0].type !== 'image/png' && e.target.files[0].type !== 'image/jpeg' && e.target.files[0].type !== 'image/jpg' && e.target.files[0].type !== 'image/webp' && e.target.files[0].type !== 'image/gif') {
            alert('O arquivo deve ser uma imagem');
            return;
        }

        setUploading(true);
        const file = e.target.files[0];
        const filename = uuidv4();
        
        try {
            // Upload to Supabase storage
            const { data, error } = await supabase.storage
                .from('files')
                .upload(`images/${filename}.png`, file);

            if (error) {
                console.error('Erro ao carregar imagem:', error.message);
                alert('Erro ao fazer upload da imagem');
                
                return;
            }

            const url = `https://iwxyugwysetdkqtrurmn.supabase.co/storage/v1/object/public/files/images/${filename}.png`;

            // Update the medias table with the new image metadata
            const { error: insertError } = await supabase
                .from('medias')
                .insert([
                    {
                        filename: filename,
                        url: url,
                         
                        user_uuid: (await supabase.auth.getSession()).data.session.user.id,
                        source: 'upload'
                    }
                ]);

            if (insertError) {
                console.error('Erro ao atualizar tabela medias:', insertError.message);
            }
             
            // Select the uploaded image automatically
            if (onSelect) onSelect(url, { url, name: file.name });
            if (onClose) onClose();
            
            // Refresh the library
            fetchMedias();
        } catch (error) {
            console.error('Erro durante o upload:', error);
            alert('Erro durante o upload da imagem');
        } finally {
            setUploading(false);
        }
    }

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
                
                <button
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
                    onClick={onClose}
                    aria-label="Fechar"
                >
                    &times;
                </button>
                <h2 className="text-lg font-semibold mb-4">Sua Biblioteca de Imagens</h2>
                {/* Tabs */}
                <div className="flex mb-4 border-b">
                    <button
                        className={`px-4 py-2 font-medium focus:outline-none ${tab === "library" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
                        onClick={() => setTab("library")}
                    >
                        Biblioteca
                    </button>
                    <button
                        className={`px-4 py-2 font-medium focus:outline-none ${tab === "upload" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
                        onClick={() => setTab("upload")}
                    >
                        Upload
                    </button>
                    <button
                        className={`px-4 py-2 font-medium focus:outline-none ${tab === "emoji" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"}`}
                        onClick={() => setTab("emoji")}
                    >
                        Emoji
                    </button>
                </div>
                {/* Tab Content */}
                {tab === "library" ? (
                    <div className="p-2 bg-gray-100 rounded min-h-[120px] overflow-y-scroll max-h-[600px]">
                        {loading ? (
                            <div className="text-center py-8 text-gray-500">Carregando imagens...</div>
                        ) : medias.length === 0 ? (
                            <div className="text-center py-8 text-gray-400">Nenhuma imagem encontrada.</div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {medias.map((media) => (
                                    <div
                                        key={media.id}
                                        className="flex flex-col items-center hover:bg-blue-200 rounded-lg border-2 border-blue-300 cursor-pointer overflow-hidden"
                                        onClick={() => {
                                            if (onSelect) onSelect(media.url, media);
                                            if (onClose) onClose();
                                        }}
                                    >
                                        <img
                                            src={media.url}
                                            alt={media.name}
                                            className="w-full h-24 object-cover rounded shadow hover:scale-105 hover:opacity-70 transition-all duration-300"
                                        />
                                        <span className="text-xs mt-1 text-gray-600 truncate w-full text-center">{media.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : tab === "upload" ? (
                    <div className="p-2 bg-gray-100 rounded min-h-[120px]">
                        <div className="flex flex-col items-center justify-center py-8">
                            <div className="w-full max-w-md">
                                <label className="block font-semibold mb-2 text-center">Upload de Imagem:</label>
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-white hover:bg-gray-50 transition-colors">
                                    
                                    {uploading ? (
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                            <span className="text-sm text-gray-500">Fazendo upload...</span>
                                        </div>
                                    ) : (
                                        <>
                                    <input
                                        type="file"
                                        className="hidden"
                                        id="file-upload"
                                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                        onChange={handleImageUpload}
                                        disabled={uploading}
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className={`cursor-pointer flex flex-col items-center ${uploading ? 'opacity-50' : ''}`}
                                    >
                                        <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <span className="text-lg font-medium text-gray-700 mb-2">
                                            {uploading ? 'Fazendo upload...' : 'Clique para fazer upload'}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            PNG, JPG, JPEG, WEBP ou GIF (máximo 15MB)
                                        </span>
                                    </label>
                                    </>
                                    )}
                                </div>
                                {uploading && (
                                    <div className="mt-4 text-center">
                                        <div className="inline-flex items-center">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                            <span className="text-sm text-gray-600">Enviando imagem...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="p-2 bg-gray-100 rounded min-h-[120px]">
                        <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 gap-2">
                            <div style={{ width: "100%" }}>
                                <Picker 
                                    data={data} 
                                    onEmojiSelect={(emoji) => {
                                        if (onSelect) onSelect(emoji.native, emoji);
                                        if (onClose) onClose();
                                    }}
                                    theme="light"
                                    previewPosition="none"
                                    skinTonePosition="none"
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
